<template>
  <div class="compliance-report">
    <!-- Header Section -->
    <div class="header-section">
      <div class="container">
        <h1 class="main-title">
          <i class="fas fa-shield-alt"></i>
          Live Compliance Report
        </h1>
        <p class="subtitle">Real-time vendor policy compliance status</p>
        
        <!-- Filters -->
        <div class="filters-section">
          <div class="filter-group" v-if="userProject">
            <label>Your Project:</label>
            <div class="project-display">
              <i class="fas fa-folder"></i>
              Project {{ userProject.project_id }}
            </div>
          </div>
          <div class="filter-group">
            <label for="vendor-filter">Vendor Name:</label>
            <input 
              id="vendor-filter"
              v-model="filters.vendorName" 
              type="text" 
              placeholder="Search vendor name"
              @input="debouncedFetch"
            />
          </div>
          <div class="filter-group">
            <label for="status-filter">Status:</label>
            <select 
              id="status-filter"
              v-model="filters.status" 
              @change="debouncedFetch"
            >
              <option value="">All Statuses</option>
              <option value="Compliant">Compliant</option>
              <option value="Do Not Pay">Do Not Pay</option>
            </select>
          </div>
          <button @click="refreshData" class="refresh-btn" :disabled="loading">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
            Refresh
          </button>
        </div>
        
        <!-- Statistics Cards -->
        <div class="stats-cards" v-if="statistics">
          <div class="stat-card">
            <div class="stat-value">{{ statistics.total_vendors }}</div>
            <div class="stat-label">Total Vendors</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ statistics.total_policies }}</div>
            <div class="stat-label">Total Policies</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ statistics.accepted_policies }}</div>
            <div class="stat-label">Accepted Policies</div>
          </div>
          <div class="stat-card" :class="getComplianceClass(statistics.compliance_rate)">
            <div class="stat-value">{{ statistics.compliance_rate }}%</div>
            <div class="stat-label">Compliance Rate</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="content-section">
      <div class="container">
        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <i class="fas fa-spinner fa-spin"></i>
          <p>Loading compliance data...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
          <i class="fas fa-exclamation-triangle"></i>
          <p>{{ error }}</p>
          <button @click="fetchData" class="retry-btn">Try Again</button>
        </div>

        <!-- Vendors Table -->
        <div v-else-if="vendors.length > 0" class="vendors-table-container">
          <h2>Vendor Compliance Details</h2>
          <div class="table-wrapper">
            <table class="vendors-table">
              <thead>
                <tr>
                  <th>Vendor Name</th>
                  <th>Project ID</th>
                  <th>Status</th>
                  <th>Total Policies</th>
                  <th>Accepted</th>
                  <th>Compliance Rate</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(vendor, index) in validVendors" :key="'vendor-' + index">
                  <tr>
                    <td class="vendor-name">{{ vendor.vendor_name || 'N/A' }}</td>
                    <td>{{ vendor.project_id || 'N/A' }}</td>
                    <td>
                      <span class="status-badge" :class="getStatusClass(vendor.computed_status)">
                        {{ vendor.computed_status || 'Unknown' }}
                      </span>
                    </td>
                    <td>{{ vendor.total_policies || 0 }}</td>
                    <td>{{ vendor.accepted_policies || 0 }}</td>
                    <td>
                      <span class="compliance-rate" :class="getComplianceClass(vendor.compliance_rate)">
                        {{ vendor.compliance_rate || 0 }}%
                      </span>
                    </td>
                    <td>{{ formatDate(vendor.last_updated) }}</td>
                    <td>
                      <button 
                        @click="togglePolicyDetails(vendor.verification_id)"
                        class="toggle-details-btn"
                      >
                        <i class="fas" :class="showDetails[vendor.verification_id] ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                        Details
                      </button>
                    </td>
                  </tr>
                  <!-- Policy Details Row -->
                  <tr v-show="showDetails[vendor.verification_id]" class="policy-details-row">
                  <td colspan="8">
                    <div class="policy-details">
                      <h4>Policies for {{ vendor?.vendor_name || 'Unknown Vendor' }}</h4>
                      <div v-if="vendor?.policies && vendor.policies.length > 0" class="policies-grid">
                        <div v-for="policy in vendor.policies" :key="policy?.policy_id || Math.random()" class="policy-card">
                                                     <div class="policy-header">
                             <span class="policy-number">{{ policy?.policy_name || 'N/A' }}</span>
                             <span class="policy-type">{{ policy?.policy_status_group || 'Unknown' }}</span>
                             <span class="acceptance-status" :class="{ accepted: policy?.policy_status_group === 'complete', rejected: policy?.policy_status_group !== 'complete' }">
                               {{ policy?.policy_status_group === 'complete' ? 'COMPLIANT' : 'NOT COMPLIANT' }}
                             </span>
                           </div>
                           <div class="policy-details-grid">
                             <div><strong>Agent:</strong> {{ policy?.agent_email || 'N/A' }}</div>
                             <div><strong>Status:</strong> {{ policy?.policy_status_group || 'Unknown' }}</div>
                             <div><strong>Project:</strong> {{ policy?.project_title || 'N/A' }}</div>
                             <div><strong>Expires:</strong> {{ formatDate(policy?.expiration) }}</div>
                           </div>
                        </div>
                      </div>
                      <p v-else class="no-policies">No policies found for this vendor.</p>
                    </div>
                  </td>
                </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state">
          <i class="fas fa-search"></i>
          <h3>No compliance data found</h3>
          <p>Try adjusting your filters or check your database connection.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { debounce } from 'lodash'
import api from '@/services/api'

export default {
  name: 'LiveComplianceReport',
  setup() {
    const loading = ref(false)
    const error = ref(null)
    const vendors = ref([])
    const statistics = ref(null)
    const userProject = ref(null)
    const showDetails = reactive({})
    
    // Computed property to filter out undefined vendors
    const validVendors = computed(() => {
      if (!vendors.value || !Array.isArray(vendors.value)) {
        return []
      }
      return vendors.value.filter(vendor => vendor && vendor.verification_id)
    })
    
    const filters = reactive({
      vendorName: '',
      status: ''
    })

    const fetchData = async () => {
      loading.value = true
      error.value = null
      
      try {
        const params = new URLSearchParams()
        if (filters.vendorName) params.append('vendor_name', filters.vendorName)
        if (filters.status) params.append('status', filters.status)
        
        // Use the configured api service to ensure auth headers are sent
        const response = await api.get(`/compliance/live-report/?${params}`)
        
        const result = response.data
        
        if (result.success) {
          vendors.value = result.data.vendors
          statistics.value = result.data.statistics
          userProject.value = result.data.user_project
        } else {
          error.value = result.error || 'Failed to fetch compliance data'
        }
      } catch (err) {
        if (err.response && err.response.status === 401) {
          error.value = 'Authentication failed. Please log in again.'
        } else {
          error.value = 'Network error: ' + (err.message || 'Unknown error')
        }
      } finally {
        loading.value = false
      }
    }

    const debouncedFetch = debounce(fetchData, 500)
    
    const refreshData = () => {
      fetchData()
    }

    const togglePolicyDetails = (verificationId) => {
      showDetails[verificationId] = !showDetails[verificationId]
    }

    const getStatusClass = (status) => {
      const statusMap = {
        'compliant': 'status-success',
        'do not pay': 'status-danger',
        'completed': 'status-success',
        'pending': 'status-warning',
        'expired': 'status-danger',
        'active': 'status-success'
      }
      return statusMap[status?.toLowerCase()] || 'status-neutral'
    }

    const getComplianceClass = (rate) => {
      if (rate >= 90) return 'compliance-excellent'
      if (rate >= 70) return 'compliance-good'
      if (rate >= 50) return 'compliance-warning'
      return 'compliance-poor'
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }

    // Fetch data automatically on mount for authenticated user
    onMounted(() => {
      fetchData()
    })

    return {
      loading,
      error,
      vendors,
      validVendors,
      statistics,
      userProject,
      filters,
      showDetails,
      fetchData,
      debouncedFetch,
      refreshData,
      togglePolicyDetails,
      getStatusClass,
      getComplianceClass,
      formatDate
    }
  }
}
</script>

<style scoped>
.compliance-report {
  min-height: 100vh;
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
}

/* Header Section */
.header-section {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 2rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
}

.main-title {
  color: white;
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  text-align: center;
  font-weight: 700;
}

.main-title i {
  margin-right: 1rem;
  color: #10b981;
}

.subtitle {
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  font-size: 1.2rem;
  margin-bottom: 2rem;
}

/* Filters */
.filters-section {
  display: flex;
  gap: 1rem;
  align-items: end;
  justify-content: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-group label {
  color: white;
  font-weight: 500;
  font-size: 0.9rem;
}

.filter-group input,
.filter-group select {
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  min-width: 200px;
}

.filter-group input::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.filter-group select option {
  background: #1f2937;
  color: white;
}

.project-display {
  padding: 0.75rem;
  background: rgba(16, 185, 129, 0.2);
  border: 1px solid rgba(16, 185, 129, 0.5);
  border-radius: 8px;
  color: #10b981;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 200px;
}

.project-display i {
  font-size: 1.1rem;
}

.refresh-btn {
  padding: 0.75rem 1.5rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.refresh-btn:hover:not(:disabled) {
  background: #059669;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Statistics Cards */
.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 2rem;
}

.stat-card {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  padding: 1.5rem;
  border-radius: 12px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.stat-value {
  font-size: 2rem;
  font-weight: bold;
  color: #10b981;
  margin-bottom: 0.5rem;
}

.stat-label {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
}

/* Compliance rate colors */
.compliance-excellent .stat-value { color: #10b981; }
.compliance-good .stat-value { color: #3b82f6; }
.compliance-warning .stat-value { color: #f59e0b; }
.compliance-poor .stat-value { color: #ef4444; }

/* Content Section */
.content-section {
  padding: 3rem 0;
}

.loading-state, .error-state, .empty-state {
  text-align: center;
  padding: 3rem;
  color: white;
}

.loading-state i, .error-state i, .empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: rgba(255, 255, 255, 0.6);
}

.retry-btn {
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

/* Table Styles */
.vendors-table-container {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.vendors-table-container h2 {
  margin-bottom: 1.5rem;
  color: #1f2937;
}

.table-wrapper {
  overflow-x: auto;
}

.vendors-table {
  width: 100%;
  border-collapse: collapse;
}

.vendors-table th,
.vendors-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.vendors-table th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
}

.vendor-name {
  font-weight: 600;
  color: #1f2937;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
}

.status-success { background: #d1fae5; color: #065f46; }
.status-warning { background: #fef3c7; color: #92400e; }
.status-danger { background: #fee2e2; color: #991b1b; }
.status-neutral { background: #f3f4f6; color: #374151; }

.compliance-rate {
  font-weight: 600;
}

.toggle-details-btn {
  padding: 0.5rem 1rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
}

.toggle-details-btn:hover {
  background: #2563eb;
}

.policy-details-row {
  background: #f9fafb;
}

.policy-details {
  padding: 1.5rem;
}

.policy-details h4 {
  margin-bottom: 1rem;
  color: #1f2937;
}

.policies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}

.policy-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
}

.policy-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.policy-number {
  font-weight: 600;
  color: #1f2937;
}

.policy-type {
  background: #e5e7eb;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
}

.acceptance-status {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.acceptance-status.accepted {
  background: #d1fae5;
  color: #065f46;
}

.acceptance-status.rejected {
  background: #fee2e2;
  color: #991b1b;
}

.policy-details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.5rem;
  font-size: 0.875rem;
}

.no-policies {
  color: #6b7280;
  font-style: italic;
}

@media (max-width: 768px) {
  .main-title {
    font-size: 2rem;
  }
  
  .filters-section {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-group input {
    min-width: auto;
  }
  
  .stats-cards {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .policies-grid {
    grid-template-columns: 1fr;
  }
}
</style> 